import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart,
  Area,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import { useGroups, useConfig, useFeedback, useAllMessageLog, useAllSentimentHistory } from "../lib/api";
import { useDemoIdentity } from "../lib/DemoIdentityContext";
import { useTenantTerms } from "../lib/TenantConfigContext";
import { nb, isEscalationFlagged } from "../lib/journey";
import { pendingApprovalStage, isPendingResponse } from "../lib/queueMetrics";
import { MetricCard } from "../components/MetricCard";
import { DateRangePicker } from "../components/DateRangePicker";
import {
  GRANULARITIES,
  defaultRangeFor,
  parseCreatedAt,
  inRange,
  buildSeries,
  previousPeriod,
  dailyActivityMap,
  buildQueueSearchParams,
} from "../lib/dates";
import { computeCSAT, computeCEI, computeNPS, scoreColor } from "../lib/feedback";
import { CheckCircle2, FileText, AlertTriangle, MessageSquareHeart, Loader2, ClipboardCheck, PackageCheck, Send, MessageCircleQuestion, Download } from "lucide-react";
import { NUM } from "../lib/dashboardTokens";
import {
  buildGmReportRows,
  buildEscalationReportRows,
  buildWorkshopSummaryRows,
  buildAdvisorTotalsRows,
  downloadGmReportXlsx,
} from "../lib/gmReport";

// Fallback trend for datasets without "Created At" yet (pre-backend-deploy / Sheets mode):
// rows are appended in creation order, so bucketing by row index is a coarse time proxy.
function rowOrderTrend(rows, predicate, buckets = 10) {
  const n = rows.length;
  if (!n) return [{ v: 0 }];
  const step = Math.max(1, Math.ceil(n / buckets));
  const points = [];
  let cum = 0;
  for (let i = 0; i < n; i++) {
    if (predicate(rows[i])) cum++;
    if ((i + 1) % step === 0 || i === n - 1) points.push({ v: cum });
  }
  return points;
}

const KPI = ({ label, value, icon: Icon, tint, testid, trend, trendColor, onClick }) => {
  const interactive = typeof onClick === "function";
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      className={`rounded-xl border border-slate-200 bg-white p-3.5 sm:p-5 flex flex-col justify-between transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
        interactive ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="uplabel leading-tight">{label}</span>
        <span className={`h-7 w-7 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center shrink-0 ${tint}`}>
          <Icon size={15} strokeWidth={2.2} />
        </span>
      </div>
      <div className="flex items-end justify-between mt-4 sm:mt-6">
        <div className={`font-work text-3xl sm:text-4xl font-semibold text-slate-900 ${NUM}`}>{value}</div>
        <AreaChart width={72} height={28} data={trend} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`spark-${testid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={trendColor} stopOpacity={0.35} />
              <stop offset="100%" stopColor={trendColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={trendColor} strokeWidth={1.75} fill={`url(#spark-${testid})`} dot={false} isAnimationActive={false} />
        </AreaChart>
      </div>
    </div>
  );
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-sm">
      <div className="font-medium text-slate-800">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5 text-slate-500 tabular-nums">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
          {p.dataKey === "prev" ? "Previous period" : "Groups created"}: {p.value}
        </div>
      ))}
    </div>
  );
};

const TrendPanel = ({ data }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="panel-trend">
    <h3 className="font-work text-base font-medium text-slate-800 mb-4">Activity over time</h3>
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
        <defs>
          <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2667B5" stopOpacity={0.25} />
            <stop offset="100%" stopColor="#2667B5" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} />
        <Tooltip content={<TrendTooltip />} cursor={{ stroke: "#e2e8f0" }} />
        <Line type="monotone" dataKey="prev" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
        <Area type="monotone" dataKey="v" stroke="#2667B5" strokeWidth={2} fill="url(#trend-fill)" dot={false} animationDuration={400} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const Gauge = ({ label, sublabel, score, testid }) => {
  const color = scoreColor(score);
  const data = [{ value: score ?? 0, fill: color }];
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative h-32 w-32">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="72%" outerRadius="100%" data={data} startAngle={90} endAngle={-270} barSize={10}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" cornerRadius={8} background={{ fill: "#f1f5f9" }} animationDuration={700} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-work text-3xl font-semibold text-slate-900">{score != null ? score : "—"}</span>
          {score != null && <span className="text-[11px] text-slate-400">/ 100</span>}
        </div>
      </div>
      <div className="mt-3 text-center">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {sublabel && <div className="text-xs text-slate-500 mt-0.5">{sublabel}</div>}
      </div>
    </div>
  );
};

const SENTIMENT_DOT = { Positive: "bg-emerald-500", Neutral: "bg-amber-400", Negative: "bg-red-500" };
const CEI_DOT = { Excellent: "bg-emerald-600", "Very Good": "bg-emerald-400", Good: "bg-amber-400", Bad: "bg-red-500" };

const CxSection = ({ csat, cei, nps }) => {
  if (!csat.total) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="panel-cx-empty">
        <h3 className="font-work text-base font-medium text-slate-800 mb-1">Customer experience</h3>
        <p className="text-sm text-slate-400">No feedback responses yet — CSAT, NPS and CEI will populate once customers respond to the survey.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="panel-cx">
      <h3 className="font-work text-base font-medium text-slate-800 mb-4">Customer experience</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="flex flex-col items-center justify-center gap-4">
          <Gauge testid="gauge-csat" label="CSAT" sublabel={`${csat.total} responses, CEI + NPS`} score={csat.pct} />
          <div className="flex items-center gap-3 text-xs text-slate-600">
            {["Positive", "Neutral", "Negative"].map((k) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${SENTIMENT_DOT[k]}`} />
                {k} {csat.sentimentCounts[k]}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-4">
          <Gauge testid="gauge-nps" label="NPS" sublabel={`${nps.promoters}/${nps.total} promoters`} score={nps.score} />
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> Promoter {nps.promoters}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500" /> Dissatisfied {nps.dissatisfied}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-4">
          <Gauge testid="gauge-cei" label="CEI" sublabel={`${cei.total} responses`} score={cei.score} />
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-600">
            {["Excellent", "Very Good", "Good", "Bad"].map((k) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${CEI_DOT[k]}`} />
                {k} {cei.counts[k]}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Overview() {
  const navigate = useNavigate();
  const { role, scopeLocations } = useDemoIdentity();
  const { unitLabelPlural } = useTenantTerms();
  const canExportReport =
    role === "master" || role === "gm" || role === "wm" || role === "ccm" || role === "psf" || role === "receptionist";
  const { data, isLoading, isError, error } = useGroups();
  const { data: cfgData } = useConfig();
  const { data: fbData } = useFeedback();
  const { data: mlData } = useAllMessageLog();
  const { data: shData } = useAllSentimentHistory();
  const [loc, setLoc] = useState("All Locations");
  const [granularity, setGranularity] = useState("Day");
  const [range, setRange] = useState(() => defaultRangeFor("Day"));

  const rows = useMemo(() => data?.rows || [], [data]);
  // "Created At" only exists once the backend has been redeployed with the Supabase
  // created_at mapping. Until then, skip date filtering instead of showing an empty dashboard.
  const hasDates = useMemo(() => rows.some((r) => parseCreatedAt(r)), [rows]);
  const activity = useMemo(() => dailyActivityMap(rows), [rows]);

  // Configured locations (Stakeholders page) may not have any groups yet — merge
  // them in so a newly added location shows up here right away, not just after its first group.
  const configLocations = useMemo(
    () => (cfgData?.rows || []).map((r) => r["Workshop Location"]).filter(nb),
    [cfgData]
  );
  const locations = useMemo(
    () => [...new Set([...configLocations, ...rows.map((r) => r["Workshop Location"]).filter(nb)])],
    [rows, configLocations]
  );
  // Non-master/gm-with-a-single-workshop roles never see other workshops' rows, even
  // via the "All Locations" pill — scope first, then apply the pill on top of that.
  const scopedRows = useMemo(
    () => (scopeLocations === "all" ? rows : rows.filter((r) => scopeLocations.includes(r["Workshop Location"]))),
    [rows, scopeLocations]
  );
  // Chip list mirrors the same scope: master/multi-location gm get "All Locations" plus
  // each of their workshops; a single-workshop role just gets that one chip.
  const locationChips = useMemo(() => {
    if (scopeLocations === "all") return ["All Locations", ...locations];
    if (scopeLocations.length <= 1) return scopeLocations;
    return ["All Locations", ...scopeLocations];
  }, [scopeLocations, locations]);
  // A single-workshop role has no "All Locations" chip to sit on — pre-select their one
  // workshop so it renders active instead of nothing looking selected.
  useEffect(() => {
    if (scopeLocations !== "all" && scopeLocations.length === 1 && loc !== scopeLocations[0]) {
      setLoc(scopeLocations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeLocations]);
  const locFiltered = loc === "All Locations" ? scopedRows : scopedRows.filter((r) => r["Workshop Location"] === loc);
  const filtered = useMemo(() => {
    if (!hasDates) return locFiltered;
    return locFiltered.filter((r) => inRange(parseCreatedAt(r), range));
  }, [locFiltered, range, hasDates]);

  const setGranularityAndRange = (g) => {
    setGranularity(g);
    setRange(defaultRangeFor(g));
  };

  // A group counts as escalated only when the classifier's explicit flag is set — see
  // lib/queueMetrics.js's buildEscalations() for why the sentiment-pattern signal (3+
  // negative messages with no flag) is no longer treated as its own escalation.
  const isEscalated = (r) => isEscalationFlagged(r["Escalation Flag"]);

  const kpis = useMemo(() => {
    return {
      created: filtered.filter((r) => nb(r["Group ID"])).length,
      jobcards: filtered.filter((r) => r["Job Card Sent"] === "Sent").length,
      // Distinct from "Job Cards Created": a booking is recorded the moment a Service Type
      // is chosen at intake, before a job card is ever sent — these previously read the
      // same kpis.jobcards value and rendered as a duplicate tile.
      servicesBooked: filtered.filter((r) => nb(r["Service Type"])).length,
      feedback: filtered.filter((r) => nb(r["Feedback Notified"])).length,
    };
  }, [filtered]);

  // Queue cards use the same workshop + created-date scope as the KPI row above. When
  // groups lack Created At (pre-backend-deploy), fall back to workshop-only so the
  // dashboard doesn't go blank. "View details" carries ?location=&from=&to= so the
  // drill-down list matches these counts.
  const queueScope = hasDates ? filtered : locFiltered;
  const approvalPendingCount = useMemo(() => queueScope.filter((r) => pendingApprovalStage(r) !== null).length, [queueScope]);
  const pendingResponseCount = useMemo(() => queueScope.filter(isPendingResponse).length, [queueScope]);
  const escalationsCount = useMemo(() => queueScope.filter(isEscalated).length, [queueScope]);
  const queueQuery = buildQueueSearchParams({ location: loc, range, hasDates });

  const trends = useMemo(() => {
    const predicates = {
      created: (r) => nb(r["Group ID"]),
      jobcards: (r) => r["Job Card Sent"] === "Sent",
      servicesBooked: (r) => nb(r["Service Type"]),
      escalations: isEscalated,
      feedback: (r) => nb(r["Feedback Notified"]),
    };
    return Object.fromEntries(
      Object.entries(predicates).map(([key, pred]) => [
        key,
        hasDates ? buildSeries(locFiltered, range, granularity, pred) : rowOrderTrend(filtered, pred),
      ])
    );
  }, [locFiltered, filtered, range, granularity, hasDates]);

  const trendSeries = useMemo(() => {
    if (!hasDates) return [];
    const current = buildSeries(locFiltered, range, granularity, () => true);
    const prevRange = previousPeriod(range);
    const prev = prevRange ? buildSeries(locFiltered, prevRange, granularity, () => true) : [];
    return current.map((d, i) => ({ ...d, prev: prev[i]?.v ?? null }));
  }, [locFiltered, range, granularity, hasDates]);

  // `feedback` has no workshop_location column, but each row carries the group's id via
  // "Group Row Id" — join back through `filtered` (already scoped by workshop + pill +
  // date range) so CSAT/NPS/CEI respect the same scope as the rest of the page.
  const filteredRowIds = useMemo(() => new Set(filtered.map((r) => Number(r._row))), [filtered]);
  const feedbackRows = useMemo(
    () => (fbData?.rows || []).filter((r) => filteredRowIds.has(Number(r["Group Row Id"]))),
    [fbData, filteredRowIds]
  );
  const csat = useMemo(() => computeCSAT(feedbackRows), [feedbackRows]);
  const cei = useMemo(() => computeCEI(feedbackRows), [feedbackRows]);
  const nps = useMemo(() => computeNPS(feedbackRows), [feedbackRows]);

  const messageLogRows = useMemo(() => mlData?.rows || [], [mlData]);

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="overview-loading">
        <Loader2 className="animate-spin" size={18} /> Loading live data…
      </div>
    );
  if (isError)
    return (
      <div data-testid="overview-error" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 text-sm">
        Failed to load groups: {error?.response?.data?.detail || error.message}
      </div>
    );

  return (
    <div className="space-y-5 sm:space-y-8" data-testid="overview-page">
      <div className="flex items-start justify-between gap-3">
        <h1 className="font-work text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Overview</h1>
        {canExportReport && (
          <button
            type="button"
            data-testid="gm-report-button"
            onClick={() =>
              downloadGmReportXlsx(
                buildGmReportRows(filtered),
                buildEscalationReportRows(filtered, shData?.rows || []),
                buildWorkshopSummaryRows(filtered, feedbackRows),
                buildAdvisorTotalsRows(filtered, feedbackRows, messageLogRows)
              )
            }
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600 bg-emerald-600 px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium text-white transition-colors hover:bg-emerald-700 shrink-0"
          >
            <Download size={14} strokeWidth={2.2} />
            <span className="hidden sm:inline">Export Report</span>
            <span className="sm:hidden">Report</span>
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="location-filter">
          {locationChips.map((l) => (
            <button
              key={l}
              data-testid={`loc-pill-${l}`}
              onClick={() => setLoc(l)}
              className={`rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-colors border whitespace-nowrap shrink-0 ${
                loc === l
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="date-controls">
          <div className="flex rounded-full border border-slate-200 bg-white p-0.5" data-testid="granularity-toggle">
            {GRANULARITIES.map((g) => (
              <button
                key={g}
                data-testid={`granularity-${g.toLowerCase()}`}
                onClick={() => setGranularityAndRange(g)}
                disabled={!hasDates}
                className={`rounded-full px-2.5 sm:px-3 py-1 text-xs sm:text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  granularity === g ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <DateRangePicker range={range} onChange={setRange} activity={activity} disabled={!hasDates} />
        </div>
      </div>
      {!hasDates && (
        <p className="text-xs text-slate-400" data-testid="dates-unavailable-note">
          Date filtering will switch on once groups carry a creation timestamp.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4">
        {/* "Job Cards Closed" below mirrors Feedback Collected until Delivered+Feedback is
            tracked as its own condition — see docs/backend-integration.md item 5. Escalations
            lives only in the queue row below now — it was showing here too, as an identical
            count and click-through, which read as two cards for the same thing. */}
        <KPI label="Groups Created" value={kpis.created} icon={CheckCircle2} tint="bg-blue-50 text-blue-600" testid="kpi-created" trend={trends.created} trendColor="#2667B5" />
        <KPI label={`${unitLabelPlural} Created`} value={kpis.jobcards} icon={FileText} tint="bg-indigo-50 text-indigo-600" testid="kpi-jobcards" trend={trends.jobcards} trendColor="#4f46e5" />
        <KPI label="Services Booked" value={kpis.servicesBooked} icon={ClipboardCheck} tint="bg-teal-50 text-teal-600" testid="kpi-services-booked" trend={trends.servicesBooked} trendColor="#0d9488" />
        <KPI label="Feedback Collected" value={kpis.feedback} icon={MessageSquareHeart} tint="bg-emerald-50 text-emerald-600" testid="kpi-feedback" trend={trends.feedback} trendColor="#059669" />
        <KPI
          label={`${unitLabelPlural} Closed`}
          value={kpis.feedback}
          icon={PackageCheck}
          tint="bg-violet-50 text-violet-600"
          testid="kpi-jobcards-closed"
          trend={trends.feedback}
          trendColor="#7c3aed"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-testid="panel-queues">
        <MetricCard
          title="Customer Approval Pending"
          count={approvalPendingCount}
          icon={Send}
          theme="blue"
          subtext="View details"
          testId="queue-card-customer-approval-pending"
          onClick={() => navigate(`/customer-approval-pending${queueQuery}`)}
        />
        <MetricCard
          title="Pending Response"
          count={pendingResponseCount}
          icon={MessageCircleQuestion}
          theme="amber"
          subtext="View details"
          testId="queue-card-pending-response"
          onClick={() => navigate(`/pending-response${queueQuery}`)}
        />
        <MetricCard
          title="Escalations"
          count={escalationsCount}
          icon={AlertTriangle}
          theme="rose"
          subtext="View details"
          testId="queue-card-escalations"
          onClick={() => navigate(`/escalations${queueQuery}`)}
        />
      </div>

      {/* Temporarily hidden — not deleted. The trend line reads as noise without more history
          behind it; TrendPanel and trendSeries above are untouched and ready to bring back. */}
      {false && hasDates && <TrendPanel data={trendSeries} />}

      <CxSection csat={csat} cei={cei} nps={nps} />
    </div>
  );
}
